import { useProjects } from "@/hooks/useProjects";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import * as Icons from 'lucide-react';
import { Loader2 } from "lucide-react";

interface ProjectSelectorProps {
  value?: string;
  onValueChange: (value: string | undefined) => void;
  brandId?: string;
  placeholder?: string;
}

export function ProjectSelector({ 
  value, 
  onValueChange, 
  brandId,
  placeholder = "Select a project" 
}: ProjectSelectorProps) {
  const { projects, isLoading } = useProjects(brandId);

  const handleValueChange = (newValue: string) => {
    if (newValue === "none") {
      onValueChange(undefined);
    } else {
      onValueChange(newValue);
    }
  };

  const selectedProject = projects.find(p => p.id === value);
  const IconComponent = selectedProject?.icon 
    ? (Icons as any)[selectedProject.icon] 
    : null;

  return (
    <Select
      value={value || "none"}
      onValueChange={handleValueChange}
      disabled={isLoading}
    >
      <SelectTrigger>
        <SelectValue placeholder={isLoading ? "Loading..." : placeholder}>
          {isLoading ? (
            <div className="flex items-center gap-2">
              <Loader2 className="h-4 w-4 animate-spin" />
              <span>Loading projects...</span>
            </div>
          ) : selectedProject ? (
            <div className="flex items-center gap-2">
              {IconComponent && <IconComponent className="h-4 w-4" />}
              <span>{selectedProject.name}</span>
            </div>
          ) : (
            <span className="text-muted-foreground">No Project</span>
          )}
        </SelectValue>
      </SelectTrigger>
      <SelectContent>
        <SelectItem value="none">
          <div className="flex items-center gap-2">
            <span className="text-muted-foreground">No Project</span>
          </div>
        </SelectItem>
        {projects.map((project) => {
          const ProjectIcon = project.icon 
            ? (Icons as any)[project.icon] 
            : Icons.Folder;
          
          return (
            <SelectItem key={project.id} value={project.id}>
              <div className="flex items-center gap-2">
                <ProjectIcon className="h-4 w-4" />
                <span>{project.name}</span>
              </div>
            </SelectItem>
          );
        })}
      </SelectContent>
    </Select>
  );
}
